import {
  Component,
  OnInit,
  Input,
  Output,
  OnChanges,
  SimpleChanges,
  EventEmitter,
  inject,
  NO_ERRORS_SCHEMA,
} from '@angular/core';
import Highcharts from 'highcharts';
import { MaskService } from '../../services/mask.service';
import { HighchartsChartComponent } from 'highcharts-angular';
import { MatSliderModule } from '@angular/material/slider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MaskitoDirective } from '@maskito/angular';
@Component({
  selector: 'app-slider-charts',
  templateUrl: './slider-charts.component.html',
  styleUrls: ['./slider-charts.component.scss'],
  standalone: true,
  imports: [
    HighchartsChartComponent,
    MatSliderModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    CurrencyPipe,
    CommonModule,
    FormsModule,
    MaskitoDirective,
    ReactiveFormsModule,
  ],
  schemas: [NO_ERRORS_SCHEMA],
})
export class SliderChartsComponent implements OnInit, OnChanges {
  private mask = inject(MaskService);

  @Input() fundData;
  @Output() mixChange = new EventEmitter();
  @Output() cashFlowChange = new EventEmitter();

  Highcharts: typeof Highcharts = Highcharts;

  compareChart: Highcharts.Options = {};
  pieChart: Highcharts.Options = {};
  premiumChart: Highcharts.Options = {};
  sliderValue = 50;
  multi;
  realData;
  editedSliderValue = 50;
  tableData = { perm: [], temp: [], prem: [] };
  dollarMask = this.mask.dollarMaskSpecs();
  ogCashFlow;
  cashFlow;
  editAmount = true;
  disableSlider = false;
  cashFlowLabel;
  updateFlag = false;

  ngOnInit() {
    Highcharts.setOptions({
      lang: {
        decimalPoint: '.',
        thousandsSep: ',',
      },
    });
    if (this.fundData?.perm && !this.fundData?.temp) {
      this.sliderValue = 100;
      this.disableSlider = true;
    } else if (!this.fundData?.perm && this.fundData?.temp) {
      this.sliderValue = 0;
      this.disableSlider = true;
    } else {
      this.sliderValue = this.fundData?.permPercent * 100;
    }

    this.multi = this.fundData?.multiplier;
    this.buildChart(this.fundData);
    this.ogCashFlow = this.fundData?.cashFlow.raw[0];
    this.cashFlow = this.fundData?.cashFlow.raw[0];
    this.cashFlowLabel = this.fundData?.cashFlowLabel;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.fundData?.perm && this.fundData?.temp) {
      if (changes['fundData'] && (this.fundData?.perm || this.fundData?.temp)) {
        this.sliderValue = this.fundData?.permPercent * 100;

        if (
          this.fundData?.permPercent !== 1 ||
          this.fundData?.permPercent !== 0
        ) {
          this.editedSliderValue = this.fundData?.permPercent;
        }

        this.updateChart({
          value: this.fundData?.permPercent * 100,
          blockEmit: true,
        });
      }
    }
  }

  buildChart(data?, updatePie = true) {
    const that = this;
    this.pieChart = {
      chart: {
        type: 'pie',
        height: 'auto',
        backgroundColor: null,
        options3d: {
          enabled: true,
          alpha: 45,
        },
      },
      colors: ['#ffcb21', '#f77c00', '#89c540', '#6734ba', '#CD0000'],
      plotOptions: {
        pie: {
          // innerSize: 100,
          depth: 45,
          allowPointSelect: true,
          cursor: 'pointer',
          dataLabels: {
            enabled: false,
          },
          showInLegend: true,
        },
      },
      title: null,
      exporting: {
        enabled: false,
      },
      legend: {
        enabled: true,
        itemMarginBottom: 10,
        itemStyle: { fontSize: '14px', fontWeight: 'bold' },
        margin: 0,
      },
      credits: {
        enabled: false,
      },
      tooltip: {
        formatter: function () {
          return `${this.key}: <b>${Number(
            Math.round(Number(this.percentage + 'e2')) + 'e-2'
          )}% | $${that.formatMoney(
            +that.fundData?.amount * (+this.y / 100)
          )}</b>`;
        },
      },
      series: [
        {
          type: 'pie',
          name: 'Product Mix',
          data: [
            { name: 'Permanent', y: this.sliderValue },
            { name: 'Term', y: 100 - this.sliderValue },
          ],
        },
      ],
    };

    let compareXLabels = [];
    let premiumXLabels = [];

    if (
      data?.perm &&
      data?.perm.data &&
      (data?.perm.data[0].age || data?.perm.data[0].Age)
    ) {
      premiumXLabels = data?.perm.data.map(x => {
        return x.age || x.Age;
      });
      compareXLabels = premiumXLabels;
    } else if (
      data?.temp &&
      data?.temp.data &&
      (data?.temp.data[0].age || data?.temp.data[0].Age)
    ) {
      premiumXLabels = data?.temp.data.map(x => {
        return x.age || x.Age;
      });
      compareXLabels = premiumXLabels;
    }

    this.compareChart = {
      chart: {
        type: 'area',
        backgroundColor: null,
        height: '55%',
      },
      colors: ['#6734ba', '#5f8005'],
      title: {
        text: 'Cash Value &amp; Death Benefit',
      },
      exporting: {
        enabled: false,
      },
      legend: {
        layout: 'vertical',
        align: 'right',
        verticalAlign: 'top',
        x: 0,
        y: 30,
        floating: true,
        borderWidth: 1,
        backgroundColor: null,
      },
      xAxis: {
        categories: compareXLabels,
      },
      yAxis: {},
      credits: {
        enabled: false,
      },
      plotOptions: {
        area: {
          fillOpacity: 1,
          tooltip: {
            pointFormat: '{series.name} {point.y:,.2f}',
            valuePrefix: '$',
          },
          marker: {
            enabled: false,
          },
        },
      },
      series: this.formatCompareChartData(this.fundData, null, 'area'),
    };

    this.premiumChart = {
      chart: {
        type: 'area',
        backgroundColor: null,
        height: '55%',
      },
      colors: ['#84a9e8'],
      title: {
        text: 'Premium',
      },
      exporting: {
        enabled: false,
      },
      legend: {
        layout: 'vertical',
        align: 'right',
        verticalAlign: 'top',
        x: 0,
        y: 30,
        floating: true,
        borderWidth: 1,
        backgroundColor: null,
      },
      xAxis: {
        categories: premiumXLabels,
      },
      yAxis: {},
      credits: {
        enabled: false,
      },
      plotOptions: {
        area: {
          fillOpacity: 1,
          tooltip: {
            pointFormat: '{series.name} {point.y:,.2f}',
            valuePrefix: '$',
          },
          marker: {
            enabled: false,
          },
        },
      },
      series: this.formatPremiumChartData(this.fundData, null, 'area'),
    };

    this.updateFlag = true;
  }

  formatCompareChartData(data, perc, type?) {
    const multi = this.fundData?.multiplier;
    const calcedData = [
      {
        name: 'Death Benefit',
        data: [],
        format: '{series.name} {y:,.2f}',
        type: type,
        label: { enabled: false },
      },
      {
        name: 'Cash Value',
        data: [],
        format: '{series.name} {y:,.2f}',
        type: type,
        label: { enabled: false },
      },
    ];
    const permData = data?.perm ? data?.perm.data : null;
    const tempData = data?.temp ? data?.temp.data : null;
    let permPerc = data?.permPercent;
    let tempPerc = data?.tempPercent;

    if (perc && typeof perc !== 'undefined') {
      permPerc = perc.perm;
      tempPerc = perc.temp;
    }

    this.tableData.perm = [];
    this.tableData.temp = [];

    for (let i = 0; i < permData?.length; i++) {
      let permCalc = permData ? permData[i]?.CashValue * permPerc * multi : 0;
      let tempCalc;

      // const tempDeath = tempData && tempData[i] && tempData[i].DeathBenefit ? tempData[i].DeathBenefit : 0;

      if (!permData && tempData) {
        console.warn(
          'No data available for currently selected Perm product!!!'
        );
        tempCalc = tempData[i].DeathBenefit * tempPerc * multi;
      } else if (
        permData &&
        (!tempData || !tempData[i] || !tempData[i].DeathBenefit)
      ) {
        console.warn(
          'No data available for currently selected Term product!!!'
        );
        tempCalc = permData[i].DeathBenefit * permPerc * multi;
      } else {
        tempCalc =
          permData[i].DeathBenefit * permPerc * multi +
          tempData[i].DeathBenefit * tempPerc * multi;
      }

      permCalc = isNaN(permCalc) ? 0 : permCalc;
      tempCalc = isNaN(tempCalc) ? 0 : tempCalc;

      calcedData[0].data.push(tempCalc);
      calcedData[1].data.push(permCalc);

      if (i === 0) {
        if (permCalc) {
          this.tableData.perm.push(permCalc);
        } else {
          this.tableData.perm.push(0);
        }

        if (tempCalc) {
          this.tableData.temp.push(tempCalc);
        } else {
          this.tableData.temp.push(0);
        }

        if (permData[i].Age > 65) {
          this.tableData.perm[1] = false;
          this.tableData.temp[1] = false;
        }
      } else if (
        (permData && permData[i].Age === 65) ||
        (tempData && tempData[i] && tempData[i].Age === 65) ||
        (permData && permData[i].Age === 85) ||
        (tempData && tempData[i] && tempData[i].Age === 85)
      ) {
        if (permCalc) {
          this.tableData.perm.push(permCalc);
        } else {
          this.tableData.perm.push(0);
        }

        if (tempCalc) {
          this.tableData.temp.push(tempCalc);
        } else {
          this.tableData.temp.push(0);
        }
      }
    }
    return calcedData;
  }

  formatPremiumChartData(data, perc, type?) {
    const calcedData = [{ name: 'Premium', data: [], type: type }];
    const permData = data?.perm ? data?.perm.data : null;
    const tempData = data?.temp ? data?.temp.data : null;
    let permPerc = data?.permPercent;
    let tempPerc = data?.tempPercent;
    const multi = this.fundData?.multiplier;

    if (perc && typeof perc !== 'undefined') {
      permPerc = perc.perm;
      tempPerc = perc.temp;
    }
    this.tableData.prem = [];
    for (let i = 0; i < permData?.length; i++) {
      let premiumCalc;

      if (!permData && tempData) {
        console.warn(
          'No data available for currently selected Perm product!!!'
        );
        premiumCalc = tempData[i].Premium * tempPerc * multi;
      } else if (
        permData &&
        (!tempData || !tempData[i] || !tempData[i].Premium)
      ) {
        console.warn(
          'No data available for currently selected Term product!!!'
        );
        premiumCalc = permData[i].Premium * permPerc * multi;
      } else {
        premiumCalc =
          (permData[i].Premium * permPerc + tempData[i].Premium * tempPerc) *
          multi;
      }

      calcedData[0].data.push(premiumCalc);

      if (i === 0) {
        if (premiumCalc) {
          this.tableData.prem.push(premiumCalc);
        } else {
          this.tableData.prem.push(0);
        }

        if (permData[i].Age > 65) {
          this.tableData.prem[1] = false;
        }
      } else if (
        (permData && permData[i].Age === 65) ||
        (permData && permData[i].Age === 85) ||
        !permData[i].Age
      ) {
        if (premiumCalc) {
          this.tableData.prem.push(premiumCalc);
        } else {
          this.tableData.prem.push(0);
        }
      }
    }

    calcedData[0]['label'] = { enabled: false };
    return calcedData;
  }

  formatPieChartData(data, perc) {
    let permPerc = data.permPercent * 100;
    let tempPerc = data.tempPercent * 100;

    if (perc && typeof perc !== 'undefined') {
      permPerc = perc.perm;
      tempPerc = perc.temp;
    }

    const calcedData = [
      {
        type: 'pie',
        name: 'Product Mix',
        data: [
          ['Permanent', permPerc],
          ['Term', tempPerc],
        ],
      },
    ];

    return calcedData;
  }

  updateChart(ev) {
    if (!ev.blockEmit && ev.value >= 0) {
      this.mixChange.emit({
        perm: ev.value / 100,
        term: (100 - ev.value) / 100,
      });
    }

    if (ev) {
      const compare = this.formatCompareChartData(this.fundData, {
        perm: ev.value / 100,
        temp: (100 - ev.value) / 100,
      });

      if (this.compareChart && this.compareChart.series) {
        this.compareChart = {
          ...this.compareChart,
          series: this.compareChart.series.map((ser, i) => ({
            ...ser,
            data: compare[i].data,
          })),
        };
        this.updateFlag = true;
      }

      const premium = this.formatPremiumChartData(this.fundData, {
        perm: ev.value / 100,
        temp: (100 - ev.value) / 100,
      });

      if (this.premiumChart && this.premiumChart.series) {
        this.premiumChart = {
          ...this.premiumChart,
          series: [
            {
              ...this.premiumChart.series[0],
              data: premium[0].data,
            },
          ],
        };
        this.updateFlag = true;
      }

      if (this.pieChart && this.pieChart.series) {
        this.pieChart = {
          ...this.pieChart,
          series: [
            {
              ...this.pieChart.series[0],
              data: [
                { name: 'Permanent', y: ev.value },
                { name: 'Term', y: 100 - ev.value },
              ],
            },
          ],
        };
        this.updateFlag = true;
      }
    }
  }

  returnPercent(value: number): string {
    return `${value}%`;
  }

  formatMoney(amount, decimalCount = 2, decimal = '.', thousands = ',') {
    try {
      decimalCount = Math.abs(decimalCount);
      decimalCount = isNaN(decimalCount) ? 2 : decimalCount;
      const negativeSign = amount < 0 ? '-' : '';
      const i = parseInt(
        (amount = Math.abs(Number(amount) || 0).toFixed(decimalCount))
      ).toString();
      const j = i.length > 3 ? i.length % 3 : 0;
      return (
        negativeSign +
        (j ? i.substr(0, j) + thousands : '') +
        i.substr(j).replace(/(\d{3})(?=\d)/g, '$1' + thousands) +
        (decimalCount
          ? decimal +
            Math.abs(+amount - +i)
              .toFixed(decimalCount)
              .slice(2)
          : '')
      );
    } catch (e) {
      console.error(e);
    }
  }

  updateCashValue() {
    this.cashFlowChange.emit(this.removeMoneyMask(this.cashFlow));
  }

  editCashAmount() {
    this.editAmount = false;
  }

  clearEdit() {
    this.editAmount = true;
    this.cashFlow = this.ogCashFlow;
    this.cashFlowChange.emit(this.cashFlow);
  }

  removeMoneyMask(obj) {
    if (obj) {
      // eslint-disable-next-line no-useless-escape
      return Number(obj.toString().replace(/\,/gi, '').replace('$', ''));
    }
  }
}
